'use client';

import {
  Badge,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@routly/ui';
import { formatDistanceToNow } from 'date-fns';
import { Activity, AlertTriangle, HelpCircle, PlugZap, Radio } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { EmptyState, ErrorState, LoadingState, PageHeader } from '@/components/shared';
import { useClaimDevice, useFleetHealth } from '@/hooks/useFleetHealth';
import type { FleetHealth } from '@/lib/types';

const WINDOW_DAYS = 7;

/**
 * A tracker that died and a truck parked for the weekend look identical in the
 * fleet list. Here they do not: the first stops reporting, the second keeps
 * reporting a speed of zero.
 */
const STALE_AFTER_SECONDS = 60 * 60;
const OVERDUE_AFTER_SECONDS = 6 * 60 * 60;

function ago(timestamp: string): string {
  return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
}

/**
 * Is the data arriving?
 *
 * Every fact on this page had to be gathered over SSH with hand-written
 * queries: which trackers report, which have no vehicle, which vehicles have
 * gone quiet. The numbers were all present — ClickHouse holding the telemetry,
 * Postgres the fleet — and nothing joined them, so 60% of one fleet's telemetry
 * produced no trip, no event and no score for months while every dashboard
 * looked healthy.
 */
export function HealthPage() {
  const { data, isLoading, error } = useFleetHealth(WINDOW_DAYS);
  const claim = useClaimDevice();
  const [claiming, setClaiming] = useState<string | null>(null);

  const handleClaim = async (ident: string) => {
    setClaiming(ident);
    try {
      const vehicle = await claim.mutateAsync(ident);
      toast.success(`Registered ${vehicle.name}`);
    } catch (caught) {
      toast.error('Could not register the vehicle', {
        description: caught instanceof Error ? caught.message : undefined,
      });
    } finally {
      setClaiming(null);
    }
  };

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState title="Could not load ingest health" message={error.message} />;
  if (!data) return null;

  return (
    <>
      <PageHeader
        title="Ingest health"
        description="What is actually reporting, and what has gone quiet"
      />

      <IngestCard ingest={data.ingest} counts={data.counts} />

      <section className="space-y-2">
        <div>
          <h2 className="text-base font-semibold text-foreground">Devices with no vehicle</h2>
          <p className="text-sm text-muted-foreground">
            Their data is stored but never processed: no trips, no events, no scores. Register a
            vehicle and it all starts working.
          </p>
        </div>

        {data.unclaimed.length === 0 ? (
          <EmptyState
            icon={PlugZap}
            title="Every device has a vehicle"
            description="Nothing is reporting into nothing."
          />
        ) : (
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Device</TableHead>
                  <TableHead>Messages</TableHead>
                  <TableHead>Last seen</TableHead>
                  <TableHead>Top speed</TableHead>
                  <TableHead className="text-right" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.unclaimed.map((device) => (
                  <TableRow key={device.ident}>
                    <TableCell>
                      <code className="text-sm text-foreground">{device.ident}</code>
                    </TableCell>
                    <TableCell className="tabular-nums text-muted-foreground">
                      {device.messages.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {ago(device.lastSeen)}
                    </TableCell>
                    <TableCell className="tabular-nums text-muted-foreground">
                      {device.maxSpeed} km/h
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        disabled={claiming !== null}
                        onClick={() => handleClaim(device.ident)}
                      >
                        {claiming === device.ident ? 'Registering…' : 'Register'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </section>

      <section className="space-y-2">
        <div>
          <h2 className="text-base font-semibold text-foreground">Data freshness</h2>
          <p className="text-sm text-muted-foreground">Worst first.</p>
        </div>

        {data.freshness.length === 0 && data.silent.length === 0 ? (
          <EmptyState
            icon={Radio}
            title="No vehicles to report on"
            description="Register a device and it appears here."
          />
        ) : (
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>Device</TableHead>
                  <TableHead>Last seen</TableHead>
                  <TableHead>Messages</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.silent.map((vehicle) => (
                  <TableRow key={vehicle.id}>
                    <TableCell className="font-medium text-foreground">{vehicle.name}</TableCell>
                    <TableCell>
                      <code className="text-xs text-muted-foreground">{vehicle.ident ?? '—'}</code>
                    </TableCell>
                    <TableCell>
                      <Badge variant="destructive">never</Badge>
                    </TableCell>
                    <TableCell className="tabular-nums text-muted-foreground">0</TableCell>
                  </TableRow>
                ))}
                {data.freshness.map((vehicle) => (
                  <TableRow key={vehicle.id}>
                    <TableCell className="font-medium text-foreground">{vehicle.name}</TableCell>
                    <TableCell>
                      <code className="text-xs text-muted-foreground">{vehicle.ident}</code>
                    </TableCell>
                    <TableCell>
                      <Badge variant={freshnessVariant(vehicle.secondsSince)}>
                        {ago(vehicle.lastSeen)}
                      </Badge>
                    </TableCell>
                    <TableCell className="tabular-nums text-muted-foreground">
                      {vehicle.messages.toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </section>
    </>
  );
}

/**
 * A fleet that goes completely silent used to look identical to one that is
 * idle: the backend stayed healthy, `/health/ready` stayed green, and a deleted
 * DNS record cut every device off for a week without anything going red.
 */
function IngestCard({ ingest, counts }: Pick<FleetHealth, 'ingest' | 'counts'>) {
  const state = ingest?.state ?? 'unknown';
  const Icon = state === 'ok' ? Activity : state === 'stale' ? AlertTriangle : HelpCircle;
  const variant = state === 'ok' ? 'success' : state === 'stale' ? 'destructive' : 'secondary';
  const label =
    state === 'ok'
      ? 'Data is arriving'
      : state === 'stale'
        ? 'Nothing is arriving'
        : 'Not known yet';

  return (
    <Card className="p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Icon className="h-5 w-5 text-muted-foreground" />
          <div>
            <p className="font-medium text-foreground">Telemetry ingest</p>
            {ingest?.lastMessageAt ? (
              <p className="text-sm text-muted-foreground">
                Last message {ago(ingest.lastMessageAt)}
              </p>
            ) : null}
          </div>
        </div>

        <div className="flex items-center gap-5">
          <Badge variant={variant}>{label}</Badge>
          <Stat label="Reporting" value={counts.reporting} />
          <Stat label="Unclaimed" value={counts.unclaimed} />
          <Stat label="Silent" value={counts.silent} />
        </div>
      </div>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="text-right">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="font-semibold text-foreground tabular-nums">{value}</p>
    </div>
  );
}

function freshnessVariant(secondsSince: number): 'success' | 'warning' | 'destructive' {
  if (secondsSince > OVERDUE_AFTER_SECONDS) return 'destructive';
  if (secondsSince > STALE_AFTER_SECONDS) return 'warning';
  return 'success';
}
